/**
 * Manage Product Quantity Action
 * Triggered by: arc:action:manageQuantity
 */

export const runManageQuantity = async (data = {}) => {
    try {
        const { 
            action = 'set', // 'increase', 'decrease', 'set', 'reset'
            quantity = 1,
            step = 1,
            minQuantity = 1,
            maxQuantity = null,
            targetSelector = "[data-prodify-quantity-presentation]",
            autoUpdatePrice = true,
            validateRange = true 
        } = data;

        let result = {};

        switch (action) {
            case 'increase':
                result = await window.dispatchEvent(new CustomEvent('arc:action:quantityIncrease', {
                    detail: { step, maxQuantity, targetSelector, autoUpdatePrice }
                }));
                break;
            case 'decrease':
                result = await window.dispatchEvent(new CustomEvent('arc:action:quantityDecrease', {
                    detail: { step, minQuantity, targetSelector, autoUpdatePrice }
                }));
                break;
            case 'set':
                result = await window.dispatchEvent(new CustomEvent('arc:action:quantitySet', {
                    detail: { quantity, minQuantity, maxQuantity, targetSelector, autoUpdatePrice, validateRange }
                }));
                break;
            case 'reset':
                result = await window.dispatchEvent(new CustomEvent('arc:action:quantitySet', {
                    detail: { quantity: 1, minQuantity, maxQuantity, targetSelector, autoUpdatePrice, validateRange }
                }));
                break;
            default:
                throw new Error(`Unknown quantity action: ${action}`);
        }

        return {
            success: true,
            action,
            message: `Quantity ${action} completed successfully`
        };

    } catch (error) {
        console.error('Manage Quantity error:', error);
        
        window.dispatchEvent(new CustomEvent('quantity:error', {
            detail: {
                error: error.message,
                action: 'manageQuantity'
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Initialize all quantity-related event listeners
export const initializeQuantitySystem = (config = {}) => {
    const {
        targetSelector = "[data-prodify-quantity-presentation]",
        incrementButtonSelector = "[data-quantity-increment]",
        decrementButtonSelector = "[data-quantity-decrement]",
        autoSetupButtons = true
    } = config;

    // Initialize input field listeners
    initializeQuantityInputListeners(targetSelector);

    // Setup increment/decrement button listeners if requested
    if (autoSetupButtons) {
        setupQuantityButtons(incrementButtonSelector, decrementButtonSelector);
    }

    // Dispatch initialization event
    window.dispatchEvent(new CustomEvent('quantity:initialized', {
        detail: {
            targetSelector,
            incrementButtonSelector,
            decrementButtonSelector,
            timestamp: Date.now()
        }
    }));
};

// Setup quantity increment/decrement buttons
const setupQuantityButtons = (incrementSelector, decrementSelector) => {
    // Setup increment buttons
    const incrementButtons = document.querySelectorAll(incrementSelector);
    incrementButtons.forEach(button => {
        button.removeEventListener('click', handleIncrementClick);
        button.addEventListener('click', handleIncrementClick);
    });

    // Setup decrement buttons
    const decrementButtons = document.querySelectorAll(decrementSelector);
    decrementButtons.forEach(button => {
        button.removeEventListener('click', handleDecrementClick);
        button.addEventListener('click', handleDecrementClick);
    });
};

// Handle increment button clicks
const handleIncrementClick = (event) => {
    event.preventDefault();
    
    window.dispatchEvent(new CustomEvent('arc:action:quantityIncrease', {
        detail: {
            step: parseInt(event.target.dataset.step) || 1,
            autoUpdatePrice: true
        }
    }));
};

// Handle decrement button clicks
const handleDecrementClick = (event) => {
    event.preventDefault();
    
    window.dispatchEvent(new CustomEvent('arc:action:quantityDecrease', {
        detail: {
            step: parseInt(event.target.dataset.step) || 1,
            autoUpdatePrice: true
        }
    }));
};

// Initialize quantity input listeners (imported from runQuantitySet)
const initializeQuantityInputListeners = (targetSelector) => {
    const quantityInput = document.querySelector(targetSelector);
    
    if (quantityInput) {
        quantityInput.removeEventListener('change', handleQuantityInputChange);
        quantityInput.removeEventListener('input', handleQuantityInputChange);
        
        quantityInput.addEventListener('change', handleQuantityInputChange);
        quantityInput.addEventListener('input', handleQuantityInputChange);
    }
};

// Handle direct input changes
const handleQuantityInputChange = (event) => {
    const newQuantity = parseInt(event.target.value) || 1;
    
    window.dispatchEvent(new CustomEvent('arc:action:quantitySet', {
        detail: {
            quantity: newQuantity,
            autoUpdatePrice: true,
            validateRange: true
        }
    }));
};

export default runManageQuantity;
